import * as i0 from '@angular/core';
import { Directive, EventEmitter, forwardRef, Component, ChangeDetectionStrategy, Input, ViewChildren, ContentChild, ViewChild, Output, HostBinding, HostListener, NgModule } from '@angular/core';
import { NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { SiFormControlAccessor } from '@simpl/siemens-brand-ng/form-group';
import { of } from 'rxjs';
import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i2 from '@simpl/siemens-brand-localize';
import { SiLocalizationModule } from '@simpl/siemens-brand-localize';

class SiDropdownGroupOptionDirective {
    constructor(_templateRef) {
        this._templateRef = _templateRef;
    }
}
SiDropdownGroupOptionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownGroupOptionDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
SiDropdownGroupOptionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownGroupOptionDirective, selector: "[siDropdownGroupOption]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownGroupOptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdownGroupOption]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

class SiDropdownOptionDirective {
    constructor(_templateRef) {
        this._templateRef = _templateRef;
    }
}
SiDropdownOptionDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownOptionDirective, deps: [{ token: i0.TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
SiDropdownOptionDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownOptionDirective, selector: "[siDropdownOption]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownOptionDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdownOption]',
                }]
        }], ctorParameters: function () { return [{ type: i0.TemplateRef }]; } });

/**
 * This implements the Siemens Brand Dropdown style.
 * It supports the standard Angular Forms protocol, so you can use ngModel and the like.
 * Just make sure to import the Angular FormsModule or the ReactiveFormsModule in your app.
 */
// eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
class SiDropdownComponent {
    constructor(injector, element, changeDetector) {
        this.injector = injector;
        this.element = element;
        this.changeDetector = changeDetector;
        this.focussed = false;
        this.focussedChanges = of(false);
        this.trackByItem = (index, item) => item;
        this.menuHeight = '500px';
        this.multipleSelect = false;
        this.allowSelectAll = false;
        // If true a clear-icon is displayed. The default value is false.
        this.showClear = false;
        this.optionSelected = new EventEmitter();
        this.isOpen = false;
        this.focusIndex = -1;
        this.selectAll = false;
        this._invalid = null;
        this._touched = null;
        // Placeholders for the callbacks which are later provided
        // by the Control Value Accessor
        this.onTouchedCallback = () => { };
        this.onChangeCallback = () => { };
        this.valueProvider = (dropdownOption) => dropdownOption;
        this.keyProvider = (dropdownOption) => dropdownOption;
        this.groupProvider = (groupKey) => groupKey;
        this.disabledProvider = (dropdownOption) => false;
    }
    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngOnChanges(change) {
        if (change.multipleSelect) {
            if (change.multipleSelect.firstChange) {
                this.value = [];
            }
            else {
                throw new Error('MultipleSelect can only be set once. To toggle the mode create a new dropdown');
            }
        }
        if (this.multipleSelect && change.dropdownOptions && this.dropdownOptions) {
            this._keySet = new Set(this.mValue.map(this.keyProvider));
            this.writeValue(this.mValue.filter(v => this._keySet.has(this.keyProvider(v))));
            if (this._keySet.size !== this.mValue.length) {
                this.fireChange();
            }
        }
    }
    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngOnInit() {
        // Cannot be injected via constructor
        this._ngControl = this.injector.get(NgControl);
        if (!this.inputId) {
            console.warn('Missing inputId on: ', this.element.nativeElement, 'A11y features are broken');
        }
        if (this.allowSelectAll) {
            this.focusIndex = -1;
        }
    }
    // eslint-disable-next-line @angular-eslint/no-conflicting-lifecycle
    ngDoCheck() {
        if (this._ngControl && (this._ngControl.touched !== this._touched || this._ngControl.invalid !== this._invalid)) {
            this._touched = this._ngControl.touched;
            this._invalid = this._ngControl.invalid;
            this.changeDetector.markForCheck();
        }
    }
    get ngControl() {
        return this._ngControl;
    }
    get disabled() {
        return this.ngControl.disabled;
    }
    get mValue() {
        return this.value;
    }
    get sValue() {
        return this.value;
    }
    get hasValue() {
        if (this.multipleSelect) {
            return !!this.mValue.length;
        }
        else {
            return this.sValue !== null && this.sValue !== undefined;
        }
    }
    get transformedDropdownOptions() {
        return Object.keys(this.dropdownOptions).reduce((previous, current) => previous.concat(this.dropdownOptions[current]), []);
    }
    get group() {
        return this.dropdownOptions && !(this.dropdownOptions instanceof Array);
    }
    keyEvent(event) {
        switch (event.key) {
            case 'Home':
                if (this.isOpen) {
                    if (this.allowSelectAll) {
                        this.focusIndex = -1;
                    }
                    else {
                        this.focusIndex = 0;
                    }
                }
                break;
            case 'End':
                if (this.isOpen) {
                    this.focusIndex = this.transformedDropdownOptions.length - 1;
                }
                break;
            case 'ArrowDown':
            case 'Down':
                if (!this.isOpen) {
                    this.toggleOpenDropdown();
                }
                else if (this.focusIndex < this.transformedDropdownOptions.length - 1) {
                    this.focusIndex++;
                }
                else if (this.focusIndex === 0 && this.allowSelectAll) {
                    this.focusIndex = -1;
                }
                event.preventDefault();
                break;
            case 'ArrowUp':
            case 'Up':
                if (!this.isOpen) {
                    this.toggleOpenDropdown();
                }
                else if (this.focusIndex && this.focusIndex > 0) {
                    this.focusIndex--;
                }
                else if (this.focusIndex === 0 && this.allowSelectAll) {
                    this.focusIndex = -1;
                }
                event.preventDefault();
                break;
            case 'Enter':
                event.preventDefault();
                if (this.focusIndex === -1 && this.allowSelectAll) {
                    this.changeSelectAll();
                }
                else {
                    if (this.focusIndex !== -1 && !this.disabledProvider(this.transformedDropdownOptions[this.focusIndex])) {
                        this.select(event, this.transformedDropdownOptions[this.focusIndex]);
                        if (!this.multipleSelect) {
                            this.closeDropdown();
                        }
                    }
                }
                break;
            case 'Escape':
            case 'Esc':
                this.closeDropdown();
                event.stopPropagation();
                event.preventDefault();
                break;
            case 'Spacebar':
            case ' ':
                event.preventDefault();
                if (this.focusIndex === -1 && this.allowSelectAll) {
                    this.changeSelectAll();
                }
                else {
                    if (this.focusIndex !== -1 && !this.disabledProvider(this.transformedDropdownOptions[this.focusIndex])) {
                        this.select(event, this.transformedDropdownOptions[this.focusIndex]);
                        if (!this.multipleSelect) {
                            this.closeDropdown();
                        }
                    }
                }
                break;
            case 'Tab':
                // no regular close focus is lost
                this.isOpen = false;
                this.focusIndex = -1;
                break;
            default:
                if (!(this.lastKeyPress &&
                    new Date().getTime() - this.lastKeyPress.getTime() < 1000 &&
                    this.lastKeys.length > 0 &&
                    this.lastKeys[this.lastKeys.length - 1] !== event.key)) {
                    this.lastKeys = '';
                }
                // Because stupid IE is working differently than other browsers
                this.lastKeys += event.key.toLowerCase();
                this.lastKeyPress = new Date();
                // use innerText from items to match with user input
                const currentListItems = this.listItems.toArray().map(li => li.nativeElement.innerText.toLowerCase().trim());
                const optsWL = this.transformedDropdownOptions.filter((opt, index) => currentListItems[index].startsWith(this.lastKeys));
                if (optsWL.length > 0) {
                    const optI = optsWL.indexOf(this.transformedDropdownOptions[this.focusIndex]);
                    if (optI === -1 || optI === optsWL.length - 1) {
                        this.focusIndex = this.transformedDropdownOptions.indexOf(optsWL[0]);
                    }
                    else if (this.lastKeys.length === 1) {
                        this.focusIndex = this.transformedDropdownOptions.indexOf(optsWL[optI + 1]);
                    }
                }
                break;
        }
        if (this.focusIndex !== -1) {
            const item = this.listItems.toArray()[this.focusIndex].nativeElement;
            const dropdown = this.ddContainer.nativeElement;
            if (item) {
                if (item.offsetTop < dropdown.scrollTop) {
                    dropdown.scrollTop = item.offsetTop;
                }
                if (item.offsetTop + item.offsetHeight >
                    dropdown.scrollTop + dropdown.clientHeight) {
                    dropdown.scrollTop =
                        item.offsetTop + item.offsetHeight - dropdown.clientHeight;
                }
            }
        }
    }
    closeDropdown() {
        this.isOpen = false;
        this.focusIndex = -1;
        this.controlElementRef.nativeElement.focus();
        this.onTouchedCallback();
    }
    toggleOpenDropdown(event) {
        if (!(this.multipleSelect && event && event.clientX === 0 && event.clientY === 0)) {
            this.isOpen = !this.disabled && !this.isOpen;
            this.focusIndex = this.isOpen ? this.focusIndex : -1;
            if (this.isOpen) {
                setTimeout(() => this.ddContainer.nativeElement.focus());
                if (this.value && this.dropdownOptions && !this.multipleSelect) {
                    this.focusIndex = this.transformedDropdownOptions.findIndex(v => this.keyProvider(v) === this.keyProvider(this.sValue));
                }
            }
            else {
                this.controlElementRef.nativeElement.focus();
                this.onTouchedCallback();
            }
        }
    }
    select(event, opt) {
        event.stopPropagation();
        event.preventDefault();
        if (this.multipleSelect) {
            this.changeMultipleSelection(opt);
        }
        else {
            this.changeSelection(opt);
            this.closeDropdown();
        }
    }
    changeSelection(opt) {
        if (!this.valuesEqual(this.sValue, opt)) {
            this.value = opt;
            this.fireChange();
            this.optionSelected.emit(opt);
        }
    }
    changeSelectAll() {
        if (!this.multipleSelect) {
            return;
        }
        if (this.selectAll) {
            this._keySet.clear();
            this.mValue.splice(0, this.mValue.length);
        }
        else {
            let allOpts = [];
            // if group
            if (!(this.dropdownOptions instanceof Array)) {
                const currentGroup = this.dropdownOptions;
                Object.keys(currentGroup).forEach(key => {
                    allOpts = allOpts.concat(currentGroup[key]);
                });
            }
            else {
                allOpts = this.dropdownOptions;
            }
            for (const opt of allOpts.filter(o => !this.disabledProvider(o))) {
                const key = this.keyProvider(opt);
                if (!this._keySet.has(key)) {
                    this._keySet.add(key);
                    this.mValue.push(opt);
                }
            }
        }
        this.selectAll = !this.selectAll;
    }
    changeMultipleSelection(opt) {
        const key = this.keyProvider(opt);
        if (this._keySet.delete(key)) {
            this.mValue.splice(this.mValue.findIndex(v => this.valuesEqual(v, opt)), 1);
            this.selectAll = false;
        }
        else {
            this._keySet.add(key);
            this.mValue.push(opt);
            let allOpts = [];
            // if group
            if (!(this.dropdownOptions instanceof Array)) {
                const ifGroup = this.dropdownOptions;
                Object.keys(ifGroup).forEach(key2 => {
                    allOpts = allOpts.concat(ifGroup[key2]);
                });
            }
            else {
                allOpts = this.dropdownOptions;
            }
            if (allOpts.every(o => this.mValue.find(v => this.valuesEqual(v, o)))) {
                this.selectAll = true;
            }
        }
        this.fireChange();
        this.optionSelected.emit(opt);
    }
    get actualOptionTemplate() {
        return this.optionTemplate ? this.optionTemplate._templateRef : this.defaultOptionTemplate._templateRef;
    }
    get actualGroupOptionTemplate() {
        return this.groupOptionTemplate ? this.groupOptionTemplate._templateRef : this.defaultGroupOptionTemplate._templateRef;
    }
    onClick(target) {
        if (!this.element.nativeElement.contains(target) && this.isOpen) {
            this.isOpen = false;
            this.onTouchedCallback();
        }
    }
    onBlur() {
        // this ensures that a potential value change is applied before the dropdown is marked touched
        if (!this.isOpen) {
            setTimeout(() => this.onTouchedCallback());
        }
    }
    // From ControlValueAccessor interface
    writeValue(value) {
        if (this.multipleSelect) {
            this.value = [...(value || [])];
            this._keySet = new Set(this.mValue.map(this.keyProvider));
        }
        else {
            if (!this.valuesEqual(this.sValue, value)) {
                this.value = value;
            }
        }
        this.changeDetector.markForCheck();
    }
    clear(event) {
        event.stopPropagation();
        if (this.multipleSelect) {
            this.value = [];
            this._keySet.clear();
            this.fireChange();
        }
        else {
            this.changeSelection();
        }
    }
    // From ControlValueAccessor interface
    registerOnChange(fn) {
        this.onChangeCallback = fn;
    }
    // From ControlValueAccessor interface
    registerOnTouched(fn) {
        this.onTouchedCallback = fn;
    }
    // From ControlValueAccessor interface
    setDisabledState(isDisabled) {
        if (isDisabled) {
            this.isOpen = false;
            this.focusIndex = -1;
        }
        this.changeDetector.markForCheck();
    }
    valuesEqual(v1, v2) {
        if (v1 === v2) {
            return true;
        }
        if (!v1 || !v2) {
            return false;
        }
        return this.keyProvider(v1) === this.keyProvider(v2);
    }
    fireChange() {
        if (this.multipleSelect) {
            this.onChangeCallback(Array.from(this.mValue));
        }
        else {
            this.onChangeCallback(this.sValue);
        }
    }
    keepOrder(a, b) {
        return a;
    }
    getIndex(i, j) {
        const key = Object.keys(this.dropdownOptions)[i];
        const searchValue = this.dropdownOptions[key][j];
        return this.transformedDropdownOptions.map(this.keyProvider)
            .indexOf(this.keyProvider(searchValue));
    }
    isRequired() {
        return this.ngControl.control?.hasError('required');
    }
}
SiDropdownComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownComponent, deps: [{ token: i0.Injector }, { token: i0.ElementRef }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component });
SiDropdownComponent.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "15.2.4", type: SiDropdownComponent, selector: "si-dropdown", inputs: { trackBy: "trackBy", menuHeight: "menuHeight", dropdownOptions: "dropdownOptions", multipleSelect: "multipleSelect", allowSelectAll: "allowSelectAll", showClear: "showClear", valueProvider: "valueProvider", groupProvider: "groupProvider", keyProvider: "keyProvider", disabledProvider: "disabledProvider", ariaLabel: "ariaLabel", inputId: "inputId" }, outputs: { optionSelected: "optionSelected" }, host: { listeners: { "keydown": "keyEvent($event)", "document:click": "onClick($event.target)", "blur": "onBlur()" }, properties: { "class.disabled": "this.disabled", "class.si-dropdown-has-value": "this.hasValue" } }, providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            // eslint-disable-next-line @angular-eslint/no-forward-ref
            useExisting: forwardRef(() => SiDropdownComponent),
            multi: true,
        },
        {
            provide: SiFormControlAccessor,
            // eslint-disable-next-line @angular-eslint/no-forward-ref
            useExisting: forwardRef(() => SiDropdownComponent),
        }
    ], queries: [{ propertyName: "optionTemplate", first: true, predicate: SiDropdownOptionDirective, descendants: true, static: true }, { propertyName: "groupOptionTemplate", first: true, predicate: SiDropdownGroupOptionDirective, descendants: true, static: true }], viewQueries: [{ propertyName: "defaultGroupOptionTemplate", first: true, predicate: SiDropdownGroupOptionDirective, descendants: true, static: true }, { propertyName: "defaultOptionTemplate", first: true, predicate: SiDropdownOptionDirective, descendants: true, static: true }, { propertyName: "ddContainer", first: true, predicate: ["ddContainer"], descendants: true, static: true }, { propertyName: "controlElementRef", first: true, predicate: ["button"], descendants: true, static: true }, { propertyName: "listItems", predicate: ["listItems"], descendants: true }], usesOnChanges: true, ngImport: i0, template: "<!-- eslint-disable @angular-eslint/template/accessibility-role-has-required-aria -->\n<button #button\n        [attr.id]=\"inputId\"\n        class=\"form-control dropdown-toggle\"\n        type=\"button\"\n        [disabled]=\"disabled\"\n        (blur)=\"onBlur()\"\n        (click)=\"toggleOpenDropdown($event)\"\n        role=\"combobox\"\n        aria-haspopup=\"listbox\"\n        [attr.aria-expanded]=\"isOpen\"\n        [class.focus]=\"isOpen\"\n        [class.active]=\"isOpen\"\n        [class.ng-invalid]=\"_invalid\" [class.ng-touched]=\"_touched\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-required]=\"isRequired()\">\n    <ng-container *ngIf=\"!multipleSelect; else multiLabel\">\n        <ng-container *ngIf=\"sValue !== null && sValue !== undefined\" [ngTemplateOutlet]=\"actualOptionTemplate\"\n                      [ngTemplateOutletContext]=\"{value: sValue}\"></ng-container>\n    </ng-container>\n    <ng-template #multiLabel>\n        <ng-container *ngFor=\"let v of mValue, last as last; trackBy: trackByItem\">\n            <ng-container [ngTemplateOutlet]=\"actualOptionTemplate\"\n                          [ngTemplateOutletContext]=\"{value: v}\"></ng-container>\n            <ng-container *ngIf=\"!last\">,&#32;</ng-container>\n        </ng-container>\n    </ng-template>\n</button>\n<div class=\"si-input-inline-container\">\n    <button type=\"button\"\n            class=\"si-input-inline-btn\"\n            [siLocalizeAttribute]=\"{ariaLabel: 'si-dropdown.clear.aria-label'}\"\n            (click)=\"clear($event)\"\n            (keyup.enter)=\"clear($event)\"\n            *ngIf=\"showClear && value && !disabled\">\n        <span class=\"siemens-close\"></span>\n    </button>\n    <span class=\"si-input-inline-btn si-dropdown-toggle-icon\" (click)=\"toggleOpenDropdown($event)\">\n        <span class=\"siemens-drop-down\"></span>\n    </span>\n</div>\n<ul #ddContainer\n    class=\"dropdown-menu\"\n    [class.open]=\"isOpen\"\n    [class.multi]=\"this.multipleSelect\"\n    tabindex=\"-1\"\n    role=\"listbox\"\n    [attr.aria-activedescendant]=\"focusIndex !== -1 ? inputId + '_item_' + focusIndex : undefined\"\n    [attr.aria-multiselectable]=\"this.multipleSelect || undefined\"\n    [ngStyle]=\"{'overflow-y': 'auto', 'max-height': menuHeight }\">\n  <li *ngIf=\"allowSelectAll\" role=\"option\"\n      [id]=\"inputId + '_item_select_all'\"\n      [attr.aria-selected]=\"(multipleSelect && selectAll)\"\n      [ngClass]=\"{'selected' : focusIndex === -1}\">\n    <a\n        tabindex=\"-1\"\n        (click)=\"changeSelectAll()\"\n        [class.checked]=\"multipleSelect && selectAll\" class=\"dropdown-item\" >\n      <span siLocalize=\"si-dropdown.option.select.all\"> </span>\n    </a>\n  </li>\n    <ng-container *ngIf=\"group else nogroup\">\n        <ng-container *ngFor=\"let opts of dropdownOptions | keyvalue: keepOrder; trackBy: trackBy; let i = index\">\n            <li class=\"dropdown-header font-weight-bold\">\n                <ng-container [ngTemplateOutlet]=\"actualGroupOptionTemplate\"\n                              [ngTemplateOutletContext]=\"{ value: opts.key }\"></ng-container>\n            </li>\n            <li #listItems\n                role=\"option\"\n                *ngFor=\"let opt of opts.value; trackBy: trackByItem; let j = index\"\n                [id]=\"inputId + '_item_' + getIndex(i, j)\"\n                [attr.aria-selected]=\"(multipleSelect ? _keySet.has(keyProvider(opt)) : keyProvider(opt) === sValue) ? true : null\"\n                [ngClass]=\"{'selected' : getIndex(i, j) === focusIndex}\">\n                <a href=\"#\"\n                   tabindex=\"-1\"\n                   (click)=\"select($event, opt)\"\n                   [class.checked]=\"multipleSelect && _keySet.has(keyProvider(opt))\" class=\"dropdown-item\"\n                   [class.disabled]=\"disabledProvider(opt)\">\n                    <ng-container [ngTemplateOutlet]=\"actualOptionTemplate\"\n                                  [ngTemplateOutletContext]=\"{value: opt}\"></ng-container>\n                </a>\n            </li>\n        </ng-container>\n    </ng-container>\n\n    <ng-template #nogroup>\n        <ng-container *ngFor=\"let opt of dropdownOptions; trackBy: trackBy; let i = index\">\n            <li #listItems\n                role=\"option\"\n                [id]=\"inputId + '_item_' + i\"\n                [attr.aria-selected]=\"(multipleSelect ? _keySet.has(keyProvider(opt)) : keyProvider(opt) === sValue) ? true : null\"\n                [ngClass]=\"{'selected' : i === focusIndex}\">\n                <a href=\"#\"\n                   tabindex=\"-1\"\n                   (click)=\"select($event, opt)\"\n                   [class.checked]=\"multipleSelect && _keySet.has(keyProvider(opt))\" class=\"dropdown-item\"\n                   [class.disabled]=\"disabledProvider(opt)\">\n                    <ng-container [ngTemplateOutlet]=\"actualOptionTemplate\"\n                                  [ngTemplateOutletContext]=\"{value: opt}\"></ng-container>\n                </a>\n            </li>\n        </ng-container>\n    </ng-template>\n</ul>\n\n<ng-container *siDropdownOption=\"let value = value\">{{valueProvider(value)}}\n</ng-container>\n\n<ng-container *siDropdownGroupOption=\"let value = value\">{{groupProvider(value)}}\n</ng-container>\n", styles: [":host{width:100%;display:block}.dropdown-menu.open{display:block;width:100%}.checkbox-inline{padding-left:0}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgForOf, selector: "[ngFor][ngForOf]", inputs: ["ngForOf", "ngForTrackBy", "ngForTemplate"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i1.NgTemplateOutlet, selector: "[ngTemplateOutlet]", inputs: ["ngTemplateOutletContext", "ngTemplateOutlet", "ngTemplateOutletInjector"] }, { kind: "directive", type: i1.NgStyle, selector: "[ngStyle]", inputs: ["ngStyle"] }, { kind: "directive", type: i2.SiLocalizeAttributeDirective, selector: "[siLocalizeAttribute]", inputs: ["siLocalizeAttribute"] }, { kind: "directive", type: i2.SiLocalizeDirective, selector: "[siLocalize]", inputs: ["siLocalize", "siLocalizeParams"] }, { kind: "directive", type: SiDropdownGroupOptionDirective, selector: "[siDropdownGroupOption]" }, { kind: "directive", type: SiDropdownOptionDirective, selector: "[siDropdownOption]" }, { kind: "pipe", type: i1.KeyValuePipe, name: "keyvalue" }], changeDetection: i0.ChangeDetectionStrategy.OnPush });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-dropdown', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            // eslint-disable-next-line @angular-eslint/no-forward-ref
                            useExisting: forwardRef(() => SiDropdownComponent),
                            multi: true,
                        },
                        {
                            provide: SiFormControlAccessor,
                            // eslint-disable-next-line @angular-eslint/no-forward-ref
                            useExisting: forwardRef(() => SiDropdownComponent),
                        }
                    ], changeDetection: ChangeDetectionStrategy.OnPush, template: "<!-- eslint-disable @angular-eslint/template/accessibility-role-has-required-aria -->\n<button #button\n        [attr.id]=\"inputId\"\n        class=\"form-control dropdown-toggle\"\n        type=\"button\"\n        [disabled]=\"disabled\"\n        (blur)=\"onBlur()\"\n        (click)=\"toggleOpenDropdown($event)\"\n        role=\"combobox\"\n        aria-haspopup=\"listbox\"\n        [attr.aria-expanded]=\"isOpen\"\n        [class.focus]=\"isOpen\"\n        [class.active]=\"isOpen\"\n        [class.ng-invalid]=\"_invalid\" [class.ng-touched]=\"_touched\"\n        [attr.aria-label]=\"ariaLabel\"\n        [attr.aria-required]=\"isRequired()\">\n    <ng-container *ngIf=\"!multipleSelect; else multiLabel\">\n        <ng-container *ngIf=\"sValue !== null && sValue !== undefined\" [ngTemplateOutlet]=\"actualOptionTemplate\"\n                      [ngTemplateOutletContext]=\"{value: sValue}\"></ng-container>\n    </ng-container>\n    <ng-template #multiLabel>\n        <ng-container *ngFor=\"let v of mValue, last as last; trackBy: trackByItem\">\n            <ng-container [ngTemplateOutlet]=\"actualOptionTemplate\"\n                          [ngTemplateOutletContext]=\"{value: v}\"></ng-container>\n            <ng-container *ngIf=\"!last\">,&#32;</ng-container>\n        </ng-container>\n    </ng-template>\n</button>\n<div class=\"si-input-inline-container\">\n    <button type=\"button\"\n            class=\"si-input-inline-btn\"\n            [siLocalizeAttribute]=\"{ariaLabel: 'si-dropdown.clear.aria-label'}\"\n            (click)=\"clear($event)\"\n            (keyup.enter)=\"clear($event)\"\n            *ngIf=\"showClear && value && !disabled\">\n        <span class=\"siemens-close\"></span>\n    </button>\n    <span class=\"si-input-inline-btn si-dropdown-toggle-icon\" (click)=\"toggleOpenDropdown($event)\">\n        <span class=\"siemens-drop-down\"></span>\n    </span>\n</div>\n<ul #ddContainer\n    class=\"dropdown-menu\"\n    [class.open]=\"isOpen\"\n    [class.multi]=\"this.multipleSelect\"\n    tabindex=\"-1\"\n    role=\"listbox\"\n    [attr.aria-activedescendant]=\"focusIndex !== -1 ? inputId + '_item_' + focusIndex : undefined\"\n    [attr.aria-multiselectable]=\"this.multipleSelect || undefined\"\n    [ngStyle]=\"{'overflow-y': 'auto', 'max-height': menuHeight }\">\n  <li *ngIf=\"allowSelectAll\" role=\"option\"\n      [id]=\"inputId + '_item_select_all'\"\n      [attr.aria-selected]=\"(multipleSelect && selectAll)\"\n      [ngClass]=\"{'selected' : focusIndex === -1}\">\n    <a\n        tabindex=\"-1\"\n        (click)=\"changeSelectAll()\"\n        [class.checked]=\"multipleSelect && selectAll\" class=\"dropdown-item\" >\n      <span siLocalize=\"si-dropdown.option.select.all\"> </span>\n    </a>\n  </li>\n    <ng-container *ngIf=\"group else nogroup\">\n        <ng-container *ngFor=\"let opts of dropdownOptions | keyvalue: keepOrder; trackBy: trackBy; let i = index\">\n            <li class=\"dropdown-header font-weight-bold\">\n                <ng-container [ngTemplateOutlet]=\"actualGroupOptionTemplate\"\n                              [ngTemplateOutletContext]=\"{ value: opts.key }\"></ng-container>\n            </li>\n            <li #listItems\n                role=\"option\"\n                *ngFor=\"let opt of opts.value; trackBy: trackByItem; let j = index\"\n                [id]=\"inputId + '_item_' + getIndex(i, j)\"\n                [attr.aria-selected]=\"(multipleSelect ? _keySet.has(keyProvider(opt)) : keyProvider(opt) === sValue) ? true : null\"\n                [ngClass]=\"{'selected' : getIndex(i, j) === focusIndex}\">\n                <a href=\"#\"\n                   tabindex=\"-1\"\n                   (click)=\"select($event, opt)\"\n                   [class.checked]=\"multipleSelect && _keySet.has(keyProvider(opt))\" class=\"dropdown-item\"\n                   [class.disabled]=\"disabledProvider(opt)\">\n                    <ng-container [ngTemplateOutlet]=\"actualOptionTemplate\"\n                                  [ngTemplateOutletContext]=\"{value: opt}\"></ng-container>\n                </a>\n            </li>\n        </ng-container>\n    </ng-container>\n\n    <ng-template #nogroup>\n        <ng-container *ngFor=\"let opt of dropdownOptions; trackBy: trackBy; let i = index\">\n            <li #listItems\n                role=\"option\"\n                [id]=\"inputId + '_item_' + i\"\n                [attr.aria-selected]=\"(multipleSelect ? _keySet.has(keyProvider(opt)) : keyProvider(opt) === sValue) ? true : null\"\n                [ngClass]=\"{'selected' : i === focusIndex}\">\n                <a href=\"#\"\n                   tabindex=\"-1\"\n                   (click)=\"select($event, opt)\"\n                   [class.checked]=\"multipleSelect && _keySet.has(keyProvider(opt))\" class=\"dropdown-item\"\n                   [class.disabled]=\"disabledProvider(opt)\">\n                    <ng-container [ngTemplateOutlet]=\"actualOptionTemplate\"\n                                  [ngTemplateOutletContext]=\"{value: opt}\"></ng-container>\n                </a>\n            </li>\n        </ng-container>\n    </ng-template>\n</ul>\n\n<ng-container *siDropdownOption=\"let value = value\">{{valueProvider(value)}}\n</ng-container>\n\n<ng-container *siDropdownGroupOption=\"let value = value\">{{groupProvider(value)}}\n</ng-container>\n", styles: [":host{width:100%;display:block}.dropdown-menu.open{display:block;width:100%}.checkbox-inline{padding-left:0}\n"] }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i0.ElementRef }, { type: i0.ChangeDetectorRef }]; }, propDecorators: { trackBy: [{
                type: Input
            }], menuHeight: [{
                type: Input
            }], dropdownOptions: [{
                type: Input
            }], multipleSelect: [{
                type: Input
            }], allowSelectAll: [{
                type: Input
            }], showClear: [{
                type: Input
            }], listItems: [{
                type: ViewChildren,
                args: ['listItems']
            }], optionTemplate: [{
                type: ContentChild,
                args: [SiDropdownOptionDirective, { static: true }]
            }], groupOptionTemplate: [{
                type: ContentChild,
                args: [SiDropdownGroupOptionDirective, { static: true }]
            }], defaultGroupOptionTemplate: [{
                type: ViewChild,
                args: [SiDropdownGroupOptionDirective, { static: true }]
            }], defaultOptionTemplate: [{
                type: ViewChild,
                args: [SiDropdownOptionDirective, { static: true }]
            }], ddContainer: [{
                type: ViewChild,
                args: ['ddContainer', { static: true }]
            }], controlElementRef: [{
                type: ViewChild,
                args: ['button', { static: true }]
            }], optionSelected: [{
                type: Output
            }], valueProvider: [{
                type: Input
            }], groupProvider: [{
                type: Input
            }], keyProvider: [{
                type: Input
            }], disabledProvider: [{
                type: Input
            }], ariaLabel: [{
                type: Input
            }], inputId: [{
                type: Input
            }], disabled: [{
                type: HostBinding,
                args: ['class.disabled']
            }], hasValue: [{
                type: HostBinding,
                args: ['class.si-dropdown-has-value']
            }], keyEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }], onClick: [{
                type: HostListener,
                args: ['document:click', ['$event.target']]
            }], onBlur: [{
                type: HostListener,
                args: ['blur']
            }] } });

class SiDropdownModule {
}
SiDropdownModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
SiDropdownModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, declarations: [SiDropdownComponent, SiDropdownGroupOptionDirective, SiDropdownOptionDirective], imports: [CommonModule, SiLocalizationModule], exports: [SiDropdownComponent, SiDropdownOptionDirective] });
SiDropdownModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, imports: [CommonModule, SiLocalizationModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.4", ngImport: i0, type: SiDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [SiDropdownComponent, SiDropdownGroupOptionDirective, SiDropdownOptionDirective],
                    imports: [CommonModule, SiLocalizationModule],
                    exports: [SiDropdownComponent, SiDropdownOptionDirective],
                    providers: [],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiDropdownComponent, SiDropdownGroupOptionDirective, SiDropdownModule, SiDropdownOptionDirective };
